// src/views/UserViews/insights/components/InsightsHeader.tsx
//
// Title, org name, and the range selector. Per Decision #2 (plan Rule 10), there is no
// freshness/"data current through" line here - BatchFreshness is server-side only and not
// tenant-queryable, so nothing on this page implies a data-through timestamp. Per Rule 6, no
// org timezone is ever printed (e.g. no "America/Denver (MT)").
//
// The range selector has exactly four options: Yesterday / 7 days / 30 days / Custom.
// `LocalizationProvider` is mounted here, scoped to just this header, rather than at the app
// root - matching how this repo already mounts it per-view (see `Manufacturing.jsx`,
// `CreateAppVersionModal.jsx`) instead of once globally.
//
// Both pickers cap at yesterday UTC via `maxDate` (never `disableFuture`, which still permits
// today - Rule 1). Unlike `DateRangeControls.jsx`'s `endMinDate = startDate.add(1, 'day')`,
// the end picker's `minDate` is the start date itself with no `+1 day` - a single-day custom
// range (`from === to`) is valid here (Rule 3), whereas that manufacturing-metrics picker
// deliberately requires a multi-day span.
//
// Part B: the start picker's `minDate` is the org's discovered earliest data date
// (`earliestAvailable`, resolved lazily by the page shell) when known, or the fixed 365-day
// fallback floor otherwise. A helper line beneath the pickers states the available window
// when `earliestAvailable` is known, or a neutral message when it isn't - never a specific
// start date we didn't actually receive.
//
// Inline reveal (`md`+): the pickers slide out after the preset pills, inside row 1, ending
// flush with the row's right edge and pushing the pill group leftward, via
// `useMediaQuery(theme.breakpoints.up('md'))` picking between two render slots for the same
// `CustomRangeFields` component - see the row-1 comment below for why that's only safe once the
// picker block is the same height as the pill group. Below `md`, the pair still reveals beneath
// row 1 in a vertical `Collapse`, unchanged from before.
//
// Per direct user request + design review, `validateCustomRange`'s two "incomplete" failure
// reasons (`missing_from`/`missing_to` - see `insightsDateRange.ts`) no longer drive any error
// UI here at all: they fire the instant a field is empty, including immediately after selecting
// Custom before the user has touched either picker, so they describe "nothing chosen yet," not
// a mistake. Only the three "invalid" reasons (`from_after_to`, `to_after_yesterday`,
// `from_before_earliest_available` - genuine contradictions) still ring the implicated field red
// and render a caption, which now sits directly beneath that field's own column instead of in a
// single shared line under the title. `validateCustomRange` itself is unchanged - it still
// correctly gates data-fetching elsewhere on "both bounds present" - this file just stops
// treating "empty because untouched" the same as "wrong."

import { useMemo } from 'react';
import {
  Box,
  Chip,
  Collapse,
  InputAdornment,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { alpha, type Theme } from '@mui/material/styles';
import dayjs, { type Dayjs } from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { StringKeys } from '@/resources/strings/StringKeys';
import COLORS from '@/resources/style/Themes';
import {
  formatPickerDate,
  getFallbackMinSelectableDate,
  getMaxSelectableDate,
  type CustomRangeValidationFailureReason,
  type CustomRangeValidationResult,
  type InsightsRangePreset,
} from '@/models/Insights/insightsDateRange';

const DISPLAY_DATE_FORMAT = 'MMM D, YYYY';

// The Beta pill reads as a tinted status tag rather than a muted grey outline: a light blue
// surface with the app's accent blue for the label. Both colors are the existing
// `BUTTON_BACKGROUND_BLUE`/`BLUE_CRAYOLA` pairing this app already uses for tinted blue chrome
// (see GroupRoute.tsx / OrganizationManagementDetails.tsx) rather than new one-off hex values.
// The fill is that same background token at partial alpha so the 1px border in the full-
// strength token still reads as an edge against it.
//
// `textTransform` is presentation only - it never touches the translated string itself, so the
// label stays whatever the locale supplies (and the header test's `getByText` on the
// translation key still matches, since `textContent` is unchanged).
const BETA_CHIP_SX = {
  height: 22,
  borderRadius: '6px',
  color: COLORS.BLUE_CRAYOLA,
  bgcolor: alpha(COLORS.BUTTON_BACKGROUND_BLUE, 0.45),
  border: `1px solid ${COLORS.BUTTON_BACKGROUND_BLUE}`,
  '& .MuiChip-label': {
    px: 0.75,
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
} as const;

type RangeOption = { preset: InsightsRangePreset; labelKey: string };

const RANGE_OPTIONS: RangeOption[] = [
  { preset: 'yesterday', labelKey: StringKeys.INSIGHTS_RANGE_YESTERDAY },
  { preset: '7days', labelKey: StringKeys.INSIGHTS_RANGE_7_DAYS },
  { preset: '30days', labelKey: StringKeys.INSIGHTS_RANGE_30_DAYS },
  { preset: 'custom', labelKey: StringKeys.INSIGHTS_RANGE_CUSTOM },
];

// The fields are meant to read as a sibling of the `ToggleButtonGroup` pills above them, not
// as a card: full-round radius, filled `grey.100`, no border. This width just keeps a field
// from growing wider than that pill group. Raised from 150 to 180 (Fix 2), then to 230 when
// the "From"/"To" prefix moved inline as a `startAdornment` (Change 2) - the prefix eats into
// the same horizontal budget the `MM/DD/YYYY` sections need, so 180 was no longer enough
// margin. Re-verified with the same technique as Fix 2: rendered `.MuiPickersSectionList-root`
// (the `role="group"` field's sections container) with a full-year value and the "From"
// `startAdornment` in place, then compared its `scrollWidth` (content's true rendered width)
// against its `clientWidth` (the space actually available once the prefix, `RANGE_FIELD_WIDTH`,
// and the shrunk calendar `IconButton` - `RANGE_FIELD_OPEN_PICKER_BUTTON_PROPS` below - are all
// accounted for). That comparison showed overflow (`scrollWidth > clientWidth`, i.e. the
// section digits would visually clip) below ~178px even with the prefix, and zero overflow with
// a comfortable margin at 230 (`clientWidth` 134px vs. a 82px content requirement) - the extra
// headroom over the bare minimum guards against this measurement's headless-browser font
// rendering (Firefox via Playwright, per `vite.config.js`) not perfectly matching every real
// user's installed "Roboto".
//
// Re-verified again after wrapping the prefix in an `InputAdornment` (Change 1) - the 8px
// `.MuiInputAdornment-positionStart` margin that supplies the "From"/"To" gap comes out of this
// same 230px budget, on top of the prefix text itself. Same technique, real "From"/"To" copy
// (`public/locales/en/translation.json`), rendered at 1280px viewport in the real Firefox
// instance this suite already runs in: the start field's (longer "From" prefix) sections
// container now has `clientWidth` 126px against the same 82px content requirement (44px
// headroom, `scrollWidth === clientWidth`, no clipping); the end field (shorter "To" prefix) has
// even more room at `clientWidth` 144px. Both stay comfortably clear of the ~178px-without-margin
// clipping point measured above, so 230 still holds and does not need raising.
const RANGE_FIELD_WIDTH = 230;

// Shrinks the calendar-icon `IconButton` and its `InputAdornment` margin (default `medium` +
// `marginLeft: 8` per `InputAdornment.js`) to claw back the horizontal room `MM/DD/YYYY` needs
// inside `RANGE_FIELD_WIDTH` (Fix 2). Both are documented `DatePicker` slots - `slotProps`
// passed to `DatePicker` is forwarded verbatim into the underlying field's `slotProps`
// (confirmed in `useDesktopPicker.js`: `slotProps: extends({}, innerSlotProps, ...)`), which
// `PickerFieldUIContextProvider` (`PickerFieldUI.js`) reads as `slotProps.openPickerButton` /
// `slotProps.inputAdornment` alongside `slotProps.textField` - the same object, just different
// keys, not something nested under `textField`.
//
// Confirmed this stays scoped to the calendar-icon adornment and never reaches the "From"/"To"
// `InputAdornment` (Change 1): inspecting `PickerFieldUI.js` shows `slotProps.inputAdornment`
// only ever feeds the *internally constructed* start/end `InputAdornment` elements it builds
// itself for the clear/open-picker buttons (`additionalTextFieldInputProps.startAdornment` /
// `.endAdornment`), gated by `!textFieldInputProps?.startAdornment` / `!...endAdornment` - i.e.
// it only fires when the field doesn't already have one. Our "From"/"To" adornment is supplied
// as `InputProps.startAdornment` (hand-built, not through this slot), so
// `textFieldInputProps.startAdornment` is already truthy by the time `PickerFieldUI` runs,
// which short-circuits its own start-adornment construction entirely; our adornment passes
// through untouched via `resolvedTextFieldInputProps`. The open-picker button also defaults to
// `openPickerButtonPosition: 'end'` (never overridden here), so `slotProps.inputAdornment`
// resolves only into the *end* adornment - the calendar button - which is exactly what this
// constant targets.
const RANGE_FIELD_OPEN_PICKER_BUTTON_PROPS = { size: 'small' } as const;
const RANGE_FIELD_INPUT_ADORNMENT_PROPS = { sx: { ml: 0.5 } } as const;

const RANGE_FIELD_SX = {
  width: { xs: '100%', sm: RANGE_FIELD_WIDTH },
  // Targets both `.MuiOutlinedInput-root` (plain `@mui/material` `TextField`, used when
  // `enableAccessibleFieldDOMStructure` is off) and `.MuiPickersOutlinedInput-root` - the class
  // `DatePicker` actually renders by default (confirmed by inspecting the rendered DOM: the
  // accessible field's `role="group"` element carries `MuiPickersOutlinedInput-root`, not
  // `MuiOutlinedInput-root` - a different class namespace entirely, despite looking like a
  // normal outlined `TextField` in the docs/screenshots).
  //
  // Fix 1 - the error ring: `fieldset` now stays borderless in *every* state, including
  // error, rather than only by default. The outlined field always renders a `<legend>` inside
  // that `fieldset` to carve a gap for the floating label (see `Outline.js`), and that legend
  // still reserves width even with a visually-hidden label - confirmed by rendering this field
  // with a value and inspecting the DOM: `PickersOutlinedInput`'s `renderSuffix` computes
  // `notched: Boolean(notched || state.adornedStart || state.focused || state.filled)`, so once
  // the field has a value (`state.filled`), the legend's `maxWidth` transitions to `100%`
  // regardless of the `notched` prop we pass. A `fieldset` border can therefore never draw a
  // clean, unbroken ring while a value or focus is present - only removing the border entirely
  // (for every state, not just default) does. The ring itself now comes from an inset
  // `boxShadow` on the input root below, which isn't interrupted by the legend since it draws
  // around the whole padding box, not through the `fieldset` element.
  '& .MuiOutlinedInput-root, & .MuiPickersOutlinedInput-root': {
    borderRadius: 999,
    bgcolor: 'grey.100',
    '& fieldset': { border: 0 },
    '&:hover': { bgcolor: 'grey.200' },
    '&.Mui-focused': {
      bgcolor: 'grey.100',
      boxShadow: (theme: Theme) => `inset 0 0 0 2px ${theme.palette.primary.main}`,
    },
    '&.Mui-error': {
      boxShadow: (theme: Theme) => `inset 0 0 0 1px ${theme.palette.error.main}`,
    },
    '&.Mui-error.Mui-focused': {
      boxShadow: (theme: Theme) => `inset 0 0 0 2px ${theme.palette.error.main}`,
    },
  },
} as const;

// The clip-rect technique `@mui/utils`'s `visuallyHidden` export uses - copied inline rather
// than imported because `@mui/utils` isn't a direct dependency of this package (only a
// transitive one, pulled in via `@mui/material`/`@mui/x-date-pickers`) and this pnpm workspace
// doesn't hoist transitive packages into `node_modules`, so `import ... from '@mui/utils'`
// fails to resolve at build/test time (confirmed - it breaks the whole suite). Adding it as a
// direct dependency is out of scope here ("no new npm dependencies"). Unlike `display: none` or
// `visibility: hidden`, this keeps the element in the accessibility tree, which the
// `getByRole('group', { name: <label key> })` lookups in the tests depend on.
const VISUALLY_HIDDEN_SX = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const;

/** The two reasons `validateCustomRange` can return for "nothing chosen yet" rather than a
 * genuine mistake - see the file-top comment. Excluded from `CUSTOM_RANGE_ERROR_LABEL_KEYS` /
 * `CUSTOM_RANGE_ERROR_FIELD` below entirely: there is no label or field mapping for a reason
 * that never renders as an error. */
type IncompleteCustomRangeReason = 'missing_from' | 'missing_to';

/** The three reasons that still ring a field red and render a caption underneath it. */
type InvalidCustomRangeReason = Exclude<CustomRangeValidationFailureReason, IncompleteCustomRangeReason>;

function isIncompleteRangeReason(
  reason: CustomRangeValidationFailureReason,
): reason is IncompleteCustomRangeReason {
  return reason === 'missing_from' || reason === 'missing_to';
}

/** Reads the current `customRangeValidation` down to "is there an *invalid* (not merely
 * incomplete) reason to surface as an error, given the currently-selected preset". Both
 * incompleteness and non-Custom presets collapse to the same `null` - an empty, untouched
 * field needs no error styling or message (the placeholder is enough), and a stale validation
 * result must never leak through once the user has clicked off of Custom. */
function getInvalidCustomRangeError(
  isCustomSelected: boolean,
  validation: CustomRangeValidationResult,
): { reason: InvalidCustomRangeReason } | null {
  if (!isCustomSelected || validation.ok) return null;
  if (isIncompleteRangeReason(validation.reason)) return null;
  return { reason: validation.reason };
}

const CUSTOM_RANGE_ERROR_LABEL_KEYS: Record<InvalidCustomRangeReason, string> = {
  from_after_to: StringKeys.INSIGHTS_CUSTOM_RANGE_ERROR_FROM_AFTER_TO,
  to_after_yesterday: StringKeys.INSIGHTS_CUSTOM_RANGE_ERROR_TO_AFTER_YESTERDAY,
  from_before_earliest_available: StringKeys.INSIGHTS_CUSTOM_RANGE_ERROR_FROM_BEFORE_EARLIEST_AVAILABLE,
};

type RangeField = 'start' | 'end';

// Each invalid reason names exactly one field, so the caller can both flag exactly that field
// (`error` + `aria-describedby`) and position the caption itself under that same field's
// column below row 1, rather than in either field's own helper text or a line shared by both
// fields. Exactly one of `startErrorMessage`/`endErrorMessage` below is ever non-null as a
// result, which is what lets the caption stay a single element positioned per-field instead of
// two.
const CUSTOM_RANGE_ERROR_FIELD: Record<InvalidCustomRangeReason, RangeField> = {
  from_before_earliest_available: 'start',
  from_after_to: 'end',
  to_after_yesterday: 'end',
};

// Shared between the failing field's `InputProps['aria-describedby']` and the validation
// message element's own `id`, so the message stays announced with the field even though it no
// longer lives in that field's helper text. Reused as the message's `data-testid` too, matching
// the existing tests' `getByTestId('insights-custom-range-error')` lookups.
const CUSTOM_RANGE_ERROR_TEST_ID = 'insights-custom-range-error';

/** `YYYY-MM-DD` string -> the picker's controlled value. A plain date string parses as local
 * midnight of that calendar date, so this round-trips cleanly with `formatPickerDate`
 * (Trap 2) with no UTC-instant conversion in between. */
function toPickerValue(value: string | null): Dayjs | null {
  return value ? dayjs(value) : null;
}

/** Reads which field (if any) owns the current *invalid* (non-incomplete) validation failure,
 * so the caller doesn't need to branch on `customRangeError.reason` directly at each call
 * site. */
function getFieldErrorReason(
  field: RangeField,
  customRangeError: { reason: InvalidCustomRangeReason } | null,
): InvalidCustomRangeReason | null {
  if (!customRangeError) return null;
  return CUSTOM_RANGE_ERROR_FIELD[customRangeError.reason] === field
    ? customRangeError.reason
    : null;
}

/** Builds the `slotProps.textField` object for one range `DatePicker`. The field always carries
 * an inline "From"/"To" prefix via `InputProps.startAdornment` (Change 1) - replacing the
 * removed `RangeFieldGroup` caption that used to sit above the field. That caption's own height
 * was one of three things that made the picker block taller than the pill group (see the row-1
 * comment below); moving the label inline is what makes the `md`+ side-by-side reveal possible
 * without shifting the title. `errorMessage` is only ever non-null for an *invalid* reason - an
 * incomplete one (empty, untouched field) never reaches this function with one, so an empty
 * field is never flagged `error` (driving `RANGE_FIELD_SX`'s inset red ring - that class comes
 * from the `error` prop, not from helper text) and never wires up `aria-describedby` either. When
 * it is non-null, `InputProps['aria-describedby']` points at the validation caption rendered
 * below row 1, positioned under this same field's column (`CUSTOM_RANGE_ERROR_TEST_ID` -
 * `CustomRangeFieldError`); the message text itself no longer lives in this field's helper text
 * - that was the second of the three height contributors.
 *
 * `DatePicker` defaults to the accessible field DOM structure (the `role="group"` field the
 * tests query by), whose `textField` slot renders `PickersTextField`
 * (`@mui/x-date-pickers/PickersTextField`) rather than plain `@mui/material` `TextField` -
 * confirmed by inspecting `PickersTextField.js` (v8.27.2). `PickerFieldUI.js` (internal to
 * `@mui/x-date-pickers`) deletes `textFieldProps.slotProps` outright whenever the accessible
 * field DOM structure is on (the default here), so `slotProps.inputLabel` /
 * `slotProps.input` / `slotProps.textField.slotProps` are all silently dropped - only the
 * legacy top-level keys (`InputProps`, `InputLabelProps`) survive that pass. That's why the
 * label uses `InputLabelProps` below, and why the "From"/"To" prefix (Change 1) is passed as
 * `InputProps.startAdornment` rather than `slotProps.input.startAdornment`: confirmed in
 * `PickersTextField.js` - `hasStartAdornment: Boolean(startAdornment ?? InputProps?.startAdornment)`
 * reads the same legacy top-level key, and `InputProps` is spread directly onto the rendered
 * `role="group"` element (`PickersTextField.js`: `{...inputAdditionalProps, ...InputProps}`),
 * which is also how `aria-describedby` above reaches that element instead of being silently
 * dropped the way `slotProps.input['aria-describedby']` would be.
 *
 * The prefix `Typography` is itself wrapped in an `InputAdornment` (`position="start"`, imported
 * from `@mui/material`) rather than left bare - `.MuiInputAdornment-positionStart` carries MUI's
 * own `marginRight: 8px` (`InputAdornment.js`), which is the correct slot-native way to put a
 * gap between the prefix and the first date section, instead of hand-rolling an `mr`/`sx` margin
 * on the `Typography` itself. That 8px comes out of the same horizontal budget documented on
 * `RANGE_FIELD_WIDTH` below - see that constant's comment for the re-measurement this required.
 * This is unrelated to `RANGE_FIELD_INPUT_ADORNMENT_PROPS` (`slotProps.inputAdornment`, see that
 * constant's comment) - that slot only ever reaches the calendar-icon end adornment `DatePicker`
 * builds internally, never this hand-built start adornment.
 *
 * The label stays visually hidden via `VISUALLY_HIDDEN_SX` (the same clip-rect technique
 * `@mui/utils`'s `visuallyHidden` export uses internally, e.g. inside `@mui/material/Rating`)
 * rather than `display: none` or `visibility: hidden`, both of which would drop the label from
 * the accessibility tree and break the tests' `getByRole('group', { name: <label key> })`
 * lookups - the accessible name comes from `aria-labelledby` pointing at this same label
 * element. The inline "From"/"To" prefix supplies the human-readable text instead (Change 1).
 *
 * `notched: false` is passed via `InputProps` for the same legacy-key reason as above. Verified
 * empirically: a scratch render with `InputProps: { notched: false }` confirmed the prop reaches
 * `PickersOutlinedInput` (traced through `useFieldTextFieldProps` -> `PickerFieldUI` ->
 * `PickersTextField`, where `InputProps` is spread after the internally-derived
 * `notched: InputLabelProps.shrink`, so it correctly overrides that default). It does *not*,
 * however, stop the legend from widening once the field has a value: `PickersOutlinedInput`'s
 * `renderSuffix` computes `Boolean(notched || state.adornedStart || state.focused ||
 * state.filled)`, so `state.filled` alone re-forces the notch open regardless of what we pass
 * here (confirmed by inspecting the rendered `<legend>` for a filled field in the same scratch
 * render). It's kept anyway - it's the documented, correct key, and it's what actually
 * suppresses the notch for an empty, unfocused field - but the ring itself no longer depends on
 * it: `RANGE_FIELD_SX` keeps the `fieldset` borderless in every state now (Fix 1), so whether
 * the legend is notched or not is invisible either way. */
function buildRangeFieldTextFieldProps(errorMessage: string | null, prefixText: string) {
  const base = {
    size: 'small' as const,
    sx: RANGE_FIELD_SX,
    InputLabelProps: { shrink: false, sx: VISUALLY_HIDDEN_SX },
    InputProps: {
      notched: false,
      startAdornment: (
        <InputAdornment position="start">
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {prefixText}
          </Typography>
        </InputAdornment>
      ),
      ...(errorMessage !== null ? { 'aria-describedby': CUSTOM_RANGE_ERROR_TEST_ID } : {}),
    },
  };

  if (errorMessage === null) {
    return base;
  }

  return { ...base, error: true };
}

type CustomRangeFieldsProps = {
  startLabel: string;
  endLabel: string;
  fromPrefix: string;
  toPrefix: string;
  startValue: Dayjs | null;
  endValue: Dayjs | null;
  onCustomFromChange: (value: string | null) => void;
  onCustomToChange: (value: string | null) => void;
  minSelectableDate: Dayjs;
  maxSelectableDate: Dayjs;
  startErrorMessage: string | null;
  endErrorMessage: string | null;
};

/** The two range `DatePicker`s, rendered once and mounted into whichever of the two reveal
 * slots (inline beside the pills at `md`+, or in the vertical `Collapse` below row 1 otherwise
 * - see the row-1 comment) is active for the current viewport. Keeping this as a single
 * component rather than duplicating the `DatePicker` pair in both branches is what keeps the
 * two slots from drifting out of sync (props, `minDate`/`maxDate` wiring, error routing) as this
 * file changes. `direction={{ xs: 'column', sm: 'row' }}` is unchanged from before Change 5 - it
 * governs the pair's own internal layout at the narrowest widths and is orthogonal to which
 * slot (inline vs. below-row-1) the caller has mounted this into. `flexWrap: 'nowrap'` guards
 * against the pair visibly reflowing mid-animation while the inline slot's horizontal `Collapse`
 * is resizing (Change 5). */
function CustomRangeFields({
  startLabel,
  endLabel,
  fromPrefix,
  toPrefix,
  startValue,
  endValue,
  onCustomFromChange,
  onCustomToChange,
  minSelectableDate,
  maxSelectableDate,
  startErrorMessage,
  endErrorMessage,
}: CustomRangeFieldsProps) {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'nowrap' }}>
        <DatePicker
          label={startLabel}
          value={startValue}
          onChange={(value) => onCustomFromChange(value ? formatPickerDate(value) : null)}
          minDate={minSelectableDate}
          maxDate={maxSelectableDate}
          slotProps={{
            textField: buildRangeFieldTextFieldProps(startErrorMessage, fromPrefix),
            openPickerButton: RANGE_FIELD_OPEN_PICKER_BUTTON_PROPS,
            inputAdornment: RANGE_FIELD_INPUT_ADORNMENT_PROPS,
          }}
        />
        <DatePicker
          label={endLabel}
          value={endValue}
          onChange={(value) => onCustomToChange(value ? formatPickerDate(value) : null)}
          minDate={startValue ?? minSelectableDate}
          maxDate={maxSelectableDate}
          slotProps={{
            textField: buildRangeFieldTextFieldProps(endErrorMessage, toPrefix),
            openPickerButton: RANGE_FIELD_OPEN_PICKER_BUTTON_PROPS,
            inputAdornment: RANGE_FIELD_INPUT_ADORNMENT_PROPS,
          }}
        />
      </Stack>
    </LocalizationProvider>
  );
}

/** The Part B availability caption - proactive guidance that reads as prior to, and more
 * neutral than, the reactive per-field error caption below. Always `text.secondary`, never
 * red, and rendered whenever Custom is selected regardless of validation state (it is not
 * error-adjacent text, so it must never be suppressed by - or mixed into the same element as -
 * an incomplete or invalid range). Left undecorated on text alignment: both call sites below
 * render this as a lone child in its own row, so it reads flush against whichever edge that
 * row is anchored to without needing an explicit `textAlign`. */
function CustomRangeAvailabilityCaption({ text }: { text: string }) {
  return (
    <Typography variant="caption" color="text.secondary" data-testid="insights-custom-range-availability">
      {text}
    </Typography>
  );
}

/** The single per-field validation caption, positioned under the specific field column it
 * concerns (`field`) rather than centered under the title or spanning the full row - Rule 5.
 * Mirrors `CustomRangeFields`' own `Stack` shape (same `RANGE_FIELD_WIDTH` columns, same
 * `spacing={1}`, same responsive `xs`-column/`sm`+-row direction) instead of hard-coding pixel
 * offsets, so the caption tracks the real fields' layout - including their narrow-viewport
 * stacking - without duplicating `DatePicker` JSX just to measure against it. The column that
 * doesn't own the message renders empty and collapses to zero size, so only one
 * `Typography` (and one `data-testid="insights-custom-range-error"`) ever exists in the tree.
 * Text is left unaligned for the same reason as the availability caption above - each column
 * is exactly as wide as its field, so the message reads flush against that column's own left
 * edge rather than right-aligned against a sibling of a different length (the pre-existing bug
 * this replaces: the old shared-line `CustomRangeMessages` used `alignItems: 'flex-end'` to
 * right-align its two lines against *each other* inside a shrink-to-fit container, while the
 * wrapping `Box` around it left-aligned the whole block - so the two lines had mismatched left
 * edges whenever their text lengths differed). */
function CustomRangeFieldError({ field, message }: { field: RangeField; message: string }) {
  const caption = (
    <Typography
      variant="caption"
      color="error"
      id={CUSTOM_RANGE_ERROR_TEST_ID}
      data-testid={CUSTOM_RANGE_ERROR_TEST_ID}
    >
      {message}
    </Typography>
  );

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'nowrap' }}>
      <Box sx={{ width: { xs: '100%', sm: RANGE_FIELD_WIDTH } }}>{field === 'start' ? caption : null}</Box>
      <Box sx={{ width: { xs: '100%', sm: RANGE_FIELD_WIDTH } }}>{field === 'end' ? caption : null}</Box>
    </Stack>
  );
}

type InsightsHeaderProps = {
  organizationName: string | null;
  selectedPreset: InsightsRangePreset;
  onPresetChange: (preset: InsightsRangePreset) => void;
  customFrom: string | null;
  customTo: string | null;
  onCustomFromChange: (value: string | null) => void;
  onCustomToChange: (value: string | null) => void;
  customRangeValidation: CustomRangeValidationResult;
  /** The org's discovered earliest `DailyOrgRollup.event_date`, or `null` when that lookup
   * hasn't resolved yet, failed, or found no data (Part B) - see `useEarliestDataDate`. */
  earliestAvailable: string | null;
};

export default function InsightsHeader({
  organizationName,
  selectedPreset,
  onPresetChange,
  customFrom,
  customTo,
  onCustomFromChange,
  onCustomToChange,
  customRangeValidation,
  earliestAvailable,
}: InsightsHeaderProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  // Picks which of the two reveal slots is active (see the row-1 comment below). `Collapse`'s
  // `orientation` is a prop, not styleable, so the branch has to be picked in JS rather than
  // with a responsive `sx` value.
  const isWide = useMediaQuery(theme.breakpoints.up('md'));

  // Cheap and clock-derived, but memoized anyway so the two pickers' `maxDate` prop keeps a
  // stable reference across unrelated re-renders of this header.
  const maxSelectableDate = useMemo(() => dayjs(getMaxSelectableDate()), []);
  const minSelectableDateString = earliestAvailable ?? getFallbackMinSelectableDate();
  const minSelectableDate = useMemo(
    () => dayjs(minSelectableDateString),
    [minSelectableDateString],
  );
  const startValue = toPickerValue(customFrom);
  const endValue = toPickerValue(customTo);

  const isCustom = selectedPreset === 'custom';
  // Only an *invalid* reason ever reaches `customRangeError` - an incomplete one (empty,
  // untouched field) collapses to `null` here, so every downstream consumer (field `error`
  // flag, `aria-describedby`, the caption itself) automatically renders nothing for it without
  // having to re-check the tier itself.
  const customRangeError = getInvalidCustomRangeError(isCustom, customRangeValidation);
  const startErrorReason = getFieldErrorReason('start', customRangeError);
  const endErrorReason = getFieldErrorReason('end', customRangeError);
  const startErrorMessage = startErrorReason ? t(CUSTOM_RANGE_ERROR_LABEL_KEYS[startErrorReason]) : null;
  const endErrorMessage = endErrorReason ? t(CUSTOM_RANGE_ERROR_LABEL_KEYS[endErrorReason]) : null;
  const startLabel = t(StringKeys.INSIGHTS_CUSTOM_RANGE_START_LABEL);
  const endLabel = t(StringKeys.INSIGHTS_CUSTOM_RANGE_END_LABEL);
  const fromPrefix = t(StringKeys.INSIGHTS_CUSTOM_RANGE_FROM_PREFIX);
  const toPrefix = t(StringKeys.INSIGHTS_CUSTOM_RANGE_TO_PREFIX);
  // Rendered whenever Custom is selected regardless of validation state (Rule 3) - computed
  // unconditionally rather than inline in each reveal slot so both slots stay in sync.
  const availabilityText = earliestAvailable
    ? t(StringKeys.INSIGHTS_CUSTOM_RANGE_DATA_AVAILABLE, {
        from: dayjs(earliestAvailable).format(DISPLAY_DATE_FORMAT),
        to: maxSelectableDate.format(DISPLAY_DATE_FORMAT),
      })
    : t(StringKeys.INSIGHTS_CUSTOM_RANGE_DATA_AVAILABILITY_UNKNOWN);
  // `CUSTOM_RANGE_ERROR_FIELD` maps every invalid reason onto exactly one field, so at most one
  // of `startErrorMessage`/`endErrorMessage` is ever non-null - that's what lets the per-field
  // caption below stay a single element that just picks which column to render into.
  const customRangeErrorMessage = startErrorMessage ?? endErrorMessage;
  const customRangeErrorField: RangeField | null = startErrorMessage ? 'start' : endErrorMessage ? 'end' : null;

  const customRangeFields = (
    <CustomRangeFields
      startLabel={startLabel}
      endLabel={endLabel}
      fromPrefix={fromPrefix}
      toPrefix={toPrefix}
      startValue={startValue}
      endValue={endValue}
      onCustomFromChange={onCustomFromChange}
      onCustomToChange={onCustomToChange}
      minSelectableDate={minSelectableDate}
      maxSelectableDate={maxSelectableDate}
      startErrorMessage={startErrorMessage}
      endErrorMessage={endErrorMessage}
    />
  );

  return (
    // An outer column, not a single wrapping row. Row 1 (title + right-hand block) has an
    // invariant height across every preset, including Custom - that's what makes
    // `alignItems: 'center'` safe on it. At `md`+ the custom-range pickers reveal *inside* row
    // 1's right-hand block, after the pill group, via a horizontal `Collapse` (Change 5) - the
    // right-hand block's own `justifyContent: 'space-between'` pin against row 1's right edge
    // means the pickers land flush with that edge and their growth pushes the pill group
    // leftward, rather than the pickers appearing to the pill group's left. Below `md` they
    // still reveal as a sibling row beneath row 1 in a vertical `Collapse`, exactly as before.
    // Either way, revealing the pickers can never
    // reposition row 1's title, because the picker block and the pill group are now the same
    // height (~40px) - three things used to make the picker block taller than that, and all
    // three had to go: (1) the "Start date"/"End date" captions stacked above each field
    // (removed - replaced by the inline "From"/"To" `startAdornment` prefix, Change 1), (2) the
    // validation message rendered as the field's own helper text (removed - it renders below
    // row 1 now, positioned under whichever field it concerns rather than in a line shared by
    // both - see `CustomRangeFieldError`), and (3) the availability caption (unaffected in
    // height terms, but it lives below row 1 too, never inside the row-1 reveal, so it was
    // never a factor in row 1's own height once (1) and (2) were fixed). Note that neither (2)
    // nor (3) is even conditional on the tiering split below - "incomplete" reasons render no
    // caption at all, and the availability caption renders unconditionally - so that split
    // can't destabilize row 1's height either; it only changes what appears below it. `mb` is
    // intentionally omitted here: the header band's own `py: 2` in `Insights.tsx` owns that
    // spacing, so stacking another margin on top of it would leave dead space beneath the
    // availability caption.
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box
        sx={{
          display: 'flex',
          // `nowrap` from `md` up: below `md` there's no inline picker block to fit (Change 6
          // uses the separate vertical slot instead), so wrapping there is harmless and
          // unchanged from before. From `md` up, though, wrapping is exactly the bug this
          // change set out to fix by another route - discovered empirically (real-viewport
          // `Collapse` measurements, see the width/height verification in the PR notes): with
          // `wrap` still on, a viewport just above the `md` threshold (found ~900-913px with
          // this component's real English copy and an "Acme Org"-length name) has just enough
          // room for the pill group and inline pickers but not enough left over for the title,
          // so `flexWrap: 'wrap'` would drop the title to its own line - doubling row 1's
          // height and moving the title exactly the way this change must prevent, even though
          // the picker block and pill group are the same height. `nowrap` here means the title
          // can never be pushed to a second line once inline pickers are active; if the
          // right-hand block ever needs more room than a `md`-width viewport provides, it
          // overflows horizontally instead of destabilizing row 1's height.
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 1 }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
              {t(StringKeys.INSIGHTS)}
            </Typography>
            <Chip label={t(StringKeys.INSIGHTS_BETA_BADGE)} size="small" sx={BETA_CHIP_SX} />
          </Box>
          {organizationName && (
            <Typography variant="body2" color="text.secondary">
              {organizationName}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={selectedPreset}
            exclusive
            size="small"
            aria-label="Insights date range"
            onChange={(_event, nextPreset: InsightsRangePreset | null) => {
              // MUI re-emits the current value with `null` when the already-selected button is
              // clicked again - ignore that so the range can never be deselected entirely.
              if (nextPreset) onPresetChange(nextPreset);
            }}
            sx={{
              bgcolor: 'grey.100',
              borderRadius: 999,
              p: 0.5,
              '& .MuiToggleButton-root': {
                border: 0,
                borderRadius: 999,
                px: 2,
                py: 0.5,
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.secondary',
                '&:hover': { bgcolor: 'transparent', color: 'text.primary' },
              },
              '& .MuiToggleButton-root.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'common.white',
                '&:hover': { bgcolor: 'primary.dark' },
              },
            }}
          >
            {RANGE_OPTIONS.map(({ preset, labelKey }) => (
              <ToggleButton key={preset} value={preset}>
                {t(labelKey)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {isWide && (
            <Collapse in={isCustom} orientation="horizontal" unmountOnExit>
              {/* `flexShrink: 0` keeps this from being squeezed by the pill group next to it
                  while the horizontal `Collapse` is animating its width (Change 5) - without
                  it, the fields would visibly reflow mid-transition instead of just sliding
                  into view at their natural size. */}
              <Box sx={{ flexShrink: 0 }}>{customRangeFields}</Box>
            </Collapse>
          )}
        </Box>
      </Box>

      {isWide ? (
        // The pickers already reveal inline in row 1 above, flush with its right edge - only
        // the availability caption and (when invalid) the per-field error caption reveal here.
        // Ideally the availability caption would sit literally "above the fields" the way it
        // does in the narrow layout below, but the fields themselves live *inside* row 1 here
        // (that's the whole point of the invariant-height architecture above), and row 1 must
        // never gain content above it. The next best thing: render the availability caption as
        // the first/topmost line in this below-row-1 area - closest to row 1, above where the
        // reactive error caption appears - so it still reads as primary/proactive relative to
        // that error rather than as an afterthought mixed in alongside it. It stays left-aligned
        // under the title (unchanged from before) since it isn't tied to either field; the
        // error caption below it, when present, right-aligns under the specific field column it
        // concerns instead (Rule 5), since that's where the fields it's about actually sit.
        // This is the one place the band is allowed to grow: the vertical `Collapse` still
        // animates the reveal, and the band grows downward into the page content below it, but
        // row 1 above (title, org name, pill group, pickers) never moves.
        <Collapse in={isCustom} unmountOnExit>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <CustomRangeAvailabilityCaption text={availabilityText} />
            </Box>
            {customRangeErrorMessage && customRangeErrorField && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <CustomRangeFieldError field={customRangeErrorField} message={customRangeErrorMessage} />
              </Box>
            )}
          </Box>
        </Collapse>
      ) : (
        <Collapse in={isCustom} unmountOnExit>
          {/* Right-aligned on `sm`+ so the block stays visually attached to the Custom pill it
              belongs to, while its own `alignItems: 'flex-start'` keeps its contents flush to
              one left edge inside it (Change 6 - unchanged from before). The availability
              caption now leads (Rule 3 - proactive guidance directly above the field pair,
              rather than below it), then the fields, then - only when invalid - the per-field
              error caption, which uses the same column widths/spacing/direction as the fields
              themselves so it lands under the specific one it concerns (Rule 5) instead of
              spanning both or reading as attached to neither. */}
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <CustomRangeAvailabilityCaption text={availabilityText} />
              {customRangeFields}
              {customRangeErrorMessage && customRangeErrorField && (
                <CustomRangeFieldError field={customRangeErrorField} message={customRangeErrorMessage} />
              )}
            </Box>
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
